import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { 
  SUPPORTED_LANGUAGES, 
  SupportedLanguage,
  LanguageInfo 
} from "@/hooks/useUserLanguage";
import { cn } from "@/lib/utils";

interface LanguageSelectorModalProps {
  isOpen: boolean;
  onSelectLanguage: (language: SupportedLanguage) => Promise<void>;
}

const LanguageSelectorModal: React.FC<LanguageSelectorModalProps> = ({
  isOpen,
  onSelectLanguage,
}) => {
  const [selectedLang, setSelectedLang] = useState<SupportedLanguage | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSelect = async (lang: SupportedLanguage) => {
    setSelectedLang(lang);
    setIsSubmitting(true);
    
    try {
      await onSelectLanguage(lang);
      // The modal will close automatically when needsOnboarding becomes false
    } catch (err) {
      console.error("Error selecting language:", err);
      setIsSubmitting(false);
      setSelectedLang(null);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="w-full max-w-2xl"
        >
          <Card className="p-8 bg-card border-2 shadow-2xl">
            {/* Header */}
            <div className="text-center mb-8">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring" }}
                className="w-16 h-16 mx-auto mb-4 bg-primary/10 rounded-full flex items-center justify-center"
              >
                <Globe className="w-8 h-8 text-primary" />
              </motion.div>
              
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Welcome! / Willkommen!
              </h2>
              <p className="text-muted-foreground">
                Choose your preferred learning language
              </p>
              <p className="text-sm text-muted-foreground/80 mt-1">
                Wählen Sie Ihre bevorzugte Lernsprache
              </p>
            </div>

            {/* Language Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              {SUPPORTED_LANGUAGES.map((lang, index) => (
                <LanguageButton
                  key={lang.code}
                  language={lang}
                  isSelected={selectedLang === lang.code}
                  isDisabled={isSubmitting && selectedLang !== lang.code}
                  isLoading={isSubmitting && selectedLang === lang.code}
                  onClick={() => handleSelect(lang.code)}
                  delay={0.1 + index * 0.05}
                />
              ))}
            </div>

            {/* Info Text */}
            <p className="text-center text-sm text-muted-foreground">
              You can change this later in your profile settings.
              <br />
              <span className="text-xs">
                Sie können dies später in Ihren Profileinstellungen ändern.
              </span>
            </p>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

interface LanguageButtonProps {
  language: LanguageInfo;
  isSelected: boolean;
  isDisabled: boolean;
  isLoading: boolean;
  onClick: () => void;
  delay: number;
}

const LanguageButton: React.FC<LanguageButtonProps> = ({
  language,
  isSelected,
  isDisabled,
  isLoading,
  onClick,
  delay,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
    >
      <Button
        variant="outline"
        onClick={onClick}
        disabled={isDisabled}
        className={cn(
          "w-full h-auto py-4 px-4 flex flex-col items-center gap-2 transition-all duration-200",
          "hover:border-primary hover:bg-primary/5",
          isSelected && "border-primary bg-primary/10 ring-2 ring-primary ring-offset-2",
          isDisabled && !isSelected && "opacity-50"
        )}
      >
        <span className="text-4xl" role="img" aria-label={language.name}>
          {language.flag}
        </span>
        <div className="text-center">
          <div className="font-semibold text-foreground">
            {language.nativeName}
          </div>
          <div className="text-xs text-muted-foreground">
            {language.name}
          </div>
        </div>
        {isLoading && (
          <Loader2 className="w-4 h-4 animate-spin text-primary" />
        )}
        {isSelected && !isLoading && (
          <Check className="w-4 h-4 text-primary" />
        )}
      </Button>
    </motion.div>
  );
};

export default LanguageSelectorModal;
